/**
 * Does this bundle believe it is running where it is actually running?
 *
 * `/api/health` reports the Worker's own environment and cannot report the client's, because the
 * Worker never sees how the client was built. That gap is where the same failure has arrived three
 * times. The last one shipped a bundle built with `VITE_APP_ENV` unset to the staging Worker: the
 * client was pointed at `http://127.0.0.1:54321`, every sign-in died in transport, `/api/health`
 * stayed green throughout, and the login screen blamed the password.
 *
 * The check is a comparison of two facts that already exist. The bundle carries what it was built
 * against, stamped in at build time. The Worker states what it is serving as. If they disagree, the
 * deployment is wrong no matter how healthy either half looks on its own.
 */

declare const __PORTAL_CLIENT_BUILD_STAMP__: string;

export const BUILD_STAMP_PREFIX = "portal-client-build|";

export interface BuildStamp {
  appEnv: string;
  supabaseHost: string;
  /**
   * The commit this bundle was built from, or `"unknown"`.
   *
   * Added on 2026-09-09 and read by nothing in the browser. The screen below compares the
   * environment, because that is the failure a user can see: a bundle pointed at the wrong project
   * cannot sign anybody in. A bundle that is merely old signs everybody in perfectly and shows them
   * last week's interface, which no client can be expected to notice and no running process can
   * measure. Only the repository can, so `scripts/check-deployed-client.mjs` is what reads this.
   *
   * It is parsed here rather than only in that script so that one definition of the stamp's shape
   * exists, and `tests/build-stamp.test.ts` holds it to the older three field form as well: a
   * bundle deployed before this field existed must still parse, and must report its absence rather
   * than a wrong answer.
   */
  commit: string;
  raw: string;
}

export function parseBuildStamp(raw: string): BuildStamp {
  if (!raw.startsWith(BUILD_STAMP_PREFIX)) {
    return { appEnv: "unstamped", supabaseHost: "unstamped", commit: "unknown", raw };
  }

  const [appEnv = "unstamped", supabaseHost = "unstamped", commit = "unknown"] = raw
    .slice(BUILD_STAMP_PREFIX.length)
    .split("|");

  return { appEnv, supabaseHost, commit, raw };
}

/** The stamp Vite wrote into this bundle. Absent in unit tests, which import the parser directly. */
export const clientBuildStamp: BuildStamp = parseBuildStamp(
  typeof __PORTAL_CLIENT_BUILD_STAMP__ === "string" ? __PORTAL_CLIENT_BUILD_STAMP__ : ""
);

export type BuildStampVerdict =
  | { kind: "matches"; served: string }
  | { kind: "mismatch"; built: string; served: string }
  | { kind: "unstamped"; served: string }
  | { kind: "unknown" };

/**
 * `served` is the `environment` field `/api/health` already returns. Nothing is added to that
 * endpoint: the Worker has no way to know what the client was built against, and asking it to
 * report something it cannot observe would be a check that always passes.
 */
export function compareBuildStamp(stamp: BuildStamp, served: unknown): BuildStampVerdict {
  if (typeof served !== "string" || served.length === 0) {
    return { kind: "unknown" };
  }

  if (stamp.appEnv === "unstamped") {
    return { kind: "unstamped", served };
  }

  return stamp.appEnv === served
    ? { kind: "matches", served }
    : { kind: "mismatch", built: stamp.appEnv, served };
}

/** One sentence per verdict, for the screen that refuses to start. */
export function describeBuildStampVerdict(verdict: BuildStampVerdict): string | null {
  switch (verdict.kind) {
    case "mismatch":
      return (
        `Den här versionen av portalen byggdes för miljön "${verdict.built}" men serveras från ` +
        `"${verdict.served}". Den når fel Supabase-projekt och kan inte logga in någon. ` +
        "Bygg om med scripts/build-for-env.mjs och deploya igen."
      );
    case "unstamped":
      return (
        `Den här versionen av portalen bär ingen byggstämpel och serveras från "${verdict.served}". ` +
        "Den byggdes utan scripts/build-for-env.mjs och det går inte att avgöra vilket " +
        "Supabase-projekt den pekar på."
      );
    default:
      return null;
  }
}
